import React, { useState, useEffect, useContext } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CForm,
  CInput,
  CInputGroup,
  CInputGroupPrepend,
  CInputGroupText,
  CRow,
  CFormGroup,
  CLabel,
  CSpinner,
  CAlert,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import userService from "../../services/Users/userServiceClient";
import { UserContext } from "../../UserContext";

const ForceChangePassword = () => {
  // "sending" → auto-requests OTP, "otp" → enter code, "password" → enter new password
  const [step, setStep] = useState("sending");
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [otpError, setOtpError] = useState(null);
  const [canResend, setCanResend] = useState(false);

  const { user, refetchUser } = useContext(UserContext);
  const email = user?.email || "";

  useEffect(() => {
    if (email) {
      requestOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const requestOtp = async () => {
    setStep("sending");
    setLoading(true);
    setOtpError(null);
    setCanResend(false);
    await userService.forgotPassword(email);
    setLoading(false);
    // Always move to OTP step, whether success or not (toast shown on error by APICalls)
    setStep("otp");
    setOtpCode("");
  };

  const validateOtpFormat = (code) => {
    if (!code) return "Verification code is required";
    if (!/^\d{6}$/.test(code)) return "Verification code must be 6 digits";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password))
      return "Password must contain at least one lowercase letter";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter";
    if (!/[0-9]/.test(password))
      return "Password must contain at least one number";
    return "";
  };

  const validateConfirmPassword = (confirm, password) => {
    if (!confirm) return "Please confirm your password";
    if (confirm !== password) return "Passwords do not match";
    return "";
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const formatError = validateOtpFormat(otpCode);
    if (formatError) {
      setErrors({ otp: formatError });
      setTouched({ otp: true });
      return;
    }

    setLoading(true);
    setOtpError(null);
    const result = await userService.validateOtp(email, otpCode);
    setLoading(false);

    if (result.succeeded) {
      setStep("password");
      setErrors({});
      setOtpError(null);
    } else {
      const msg = result.errorMessage || "";
      const msgLower = msg.toLowerCase();

      if (msgLower.includes("too many")) {
        // Force re-send
        setOtpError(msg);
        setOtpCode("");
        await requestOtp();
      } else if (msgLower.includes("expired")) {
        setOtpError(msg);
        setCanResend(true);
      } else {
        // "Invalid verification code. N attempts remaining."
        setOtpError(msg);
      }
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    const pwdError = validatePassword(newPassword);
    const confError = validateConfirmPassword(confirmPassword, newPassword);
    if (pwdError || confError) {
      setErrors({ newPassword: pwdError, confirmPassword: confError });
      setTouched({ newPassword: true, confirmPassword: true });
      return;
    }

    setLoading(true);
    const result = await userService.resetPassword(
      email,
      otpCode,
      newPassword,
      confirmPassword,
    );
    setLoading(false);

    if (result.succeeded) {
      // Refetch user so forcePasswordChange becomes false, then navigate to dashboard
      await refetchUser();
      window.location.hash = "#/dashboard";
    } else {
      const msg = result.errorMessage || "";
      // "Passwords do not match" or other API-level errors
      setErrors({ confirmPassword: msg || "An error occurred" });
      setTouched({ confirmPassword: true });
    }
  };

  return (
    <div className="login-container">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md="6" lg="4">
            <CCard className="login-card p-4">
              <CCardHeader className="login-header text-center">
                <h3>Change Password</h3>
                <img
                  src="/logo_eif.jpg"
                  alt="EIF Logo"
                  style={{ height: "60px", marginTop: "10px" }}
                />
              </CCardHeader>
              <CCardBody>
                {/* Step: Sending OTP */}
                {step === "sending" && (
                  <div className="text-center py-4">
                    <CSpinner color="primary" />
                    <p className="mt-3 text-muted">
                      Sending verification code to <strong>{email}</strong>...
                    </p>
                  </div>
                )}

                {/* Step: Enter OTP */}
                {step === "otp" && (
                  <CForm onSubmit={handleOtpSubmit}>
                    <CAlert color="info" className="mb-3">
                      Your account requires a password change. A 6-digit
                      verification code has been sent to{" "}
                      <strong>{email}</strong>.
                    </CAlert>

                    {otpError && (
                      <CAlert color="danger" className="mb-3">
                        {otpError}
                      </CAlert>
                    )}

                    <CFormGroup>
                      <CLabel htmlFor="otpCode">Verification Code</CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-shield-alt" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          id="otpCode"
                          type="text"
                          placeholder="Enter 6-digit code"
                          maxLength="6"
                          autoComplete="one-time-code"
                          value={otpCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setOtpCode(value);
                            setOtpError(null);
                            if (touched.otp) {
                              setErrors({
                                ...errors,
                                otp: validateOtpFormat(value),
                              });
                            }
                          }}
                          onBlur={() => handleBlur("otp")}
                          invalid={(touched.otp && !!errors.otp) || !!otpError}
                        />
                      </CInputGroup>
                      {touched.otp && errors.otp && (
                        <div className="text-danger small mt-1">
                          {errors.otp}
                        </div>
                      )}
                    </CFormGroup>

                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      block
                      className="mb-3"
                    >
                      {loading ? <CSpinner size="sm" /> : "Verify Code"}
                    </CButton>

                    {canResend && (
                      <div className="text-center">
                        <CButton
                          color="link"
                          onClick={requestOtp}
                          disabled={loading}
                        >
                          Resend Code
                        </CButton>
                      </div>
                    )}
                  </CForm>
                )}

                {/* Step: New Password */}
                {step === "password" && (
                  <CForm onSubmit={handlePasswordReset}>
                    <p className="text-muted text-center mb-4">
                      Enter your new password
                    </p>

                    <CFormGroup>
                      <CLabel htmlFor="newPassword">New Password</CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-lock-locked" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          placeholder="New password"
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (touched.newPassword) {
                              setErrors({
                                ...errors,
                                newPassword: validatePassword(e.target.value),
                                confirmPassword: confirmPassword
                                  ? validateConfirmPassword(
                                      confirmPassword,
                                      e.target.value,
                                    )
                                  : "",
                              });
                            }
                          }}
                          onBlur={() => handleBlur("newPassword")}
                          invalid={touched.newPassword && !!errors.newPassword}
                        />
                        <CInputGroupPrepend>
                          <CButton
                            type="button"
                            color="secondary"
                            variant="outline"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            <img
                              src={
                                showNewPassword ? "/eye-slash.png" : "/eye.png"
                              }
                              alt={
                                showNewPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              style={{ height: "14px", width: "14px" }}
                            />
                          </CButton>
                        </CInputGroupPrepend>
                      </CInputGroup>
                      {touched.newPassword && errors.newPassword && (
                        <div className="text-danger small mt-1">
                          {errors.newPassword}
                        </div>
                      )}
                    </CFormGroup>

                    <CFormGroup>
                      <CLabel htmlFor="confirmPassword">
                        Confirm Password
                      </CLabel>
                      <CInputGroup>
                        <CInputGroupPrepend>
                          <CInputGroupText>
                            <CIcon name="cil-lock-locked" />
                          </CInputGroupText>
                        </CInputGroupPrepend>
                        <CInput
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (touched.confirmPassword) {
                              setErrors({
                                ...errors,
                                confirmPassword: validateConfirmPassword(
                                  e.target.value,
                                  newPassword,
                                ),
                              });
                            }
                          }}
                          onBlur={() => handleBlur("confirmPassword")}
                          invalid={
                            touched.confirmPassword && !!errors.confirmPassword
                          }
                        />
                        <CInputGroupPrepend>
                          <CButton
                            type="button"
                            color="secondary"
                            variant="outline"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            <img
                              src={
                                showConfirmPassword
                                  ? "/eye-slash.png"
                                  : "/eye.png"
                              }
                              alt={
                                showConfirmPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              style={{ height: "14px", width: "14px" }}
                            />
                          </CButton>
                        </CInputGroupPrepend>
                      </CInputGroup>
                      {touched.confirmPassword && errors.confirmPassword && (
                        <div className="text-danger small mt-1">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </CFormGroup>

                    <small className="text-muted d-block mb-3">
                      Password must be at least 8 characters with uppercase,
                      lowercase, and a number.
                    </small>

                    <CButton
                      color="primary"
                      type="submit"
                      disabled={loading}
                      block
                    >
                      {loading ? <CSpinner size="sm" /> : "Set New Password"}
                    </CButton>
                  </CForm>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default ForceChangePassword;
